/**
 * SmartBackupCalendar — Disaster Recovery surface.
 *
 * UX (per owner spec, 2026-05-18):
 *   • Monthly calendar grid (RTL).
 *   • Days with a snapshot are clickable green pills; days without are
 *     disabled.
 *   • Click a day → side drawer with metadata + restore CTA.
 *   • Restore → confirm modal → server takes a "pre_restore" safety
 *     snapshot → restores → post-validation report.
 *   • If a restore was performed, the day-drawer also shows an "Undo
 *     last restore" button.
 *
 * Calls:
 *   GET    /api/companies/:cid/smart-snapshots
 *   POST   /api/companies/:cid/smart-snapshots/create-now
 *   GET    /api/companies/:cid/smart-snapshots/:sid
 *   POST   /api/companies/:cid/smart-snapshots/:sid/restore
 *   POST   /api/companies/:cid/smart-snapshots/undo-last-restore
 *   GET    /api/companies/:cid/smart-snapshots/restore-history
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  X, ChevronRight, ChevronLeft, Calendar, Loader2, AlertTriangle,
  CheckCircle2, Undo2, Download, RefreshCw, Database, HardDrive,
  Cloud, ExternalLink, Plus, Upload, RotateCcw, Trash2,
} from "lucide-react";
import { useRef } from "react";
import { api } from "../lib/api";

const HE_MONTHS = [
  "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
  "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר",
];
const HE_DAYS = ["א", "ב", "ג", "ד", "ה", "ו", "ש"];

function _bytes(n) {
  if (!n) return "0KB";
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / 1024 / 1024).toFixed(2)}MB`;
}
function _fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
}

export default function SmartBackupCalendar({ companyId, onClose }) {
  const [snapshots, setSnapshots] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [postReport, setPostReport] = useState(null);
  // === Drive folder config (inline at top of calendar) ===
  const [folderUrl, setFolderUrl] = useState("");
  const [savedUrl, setSavedUrl] = useState("");
  const [savingDrive, setSavingDrive] = useState(false);
  // === Upload-restore (file picker) ===
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [confirmUpload, setConfirmUpload] = useState(null); // {file} when chosen
  // === Quota state ===
  const [quota, setQuota] = useState(null);
  const [quotaPopup, setQuotaPopup] = useState(null); // {would_add_mb, quota}
  const [upgradePopup, setUpgradePopup] = useState(false);
  const [upgradeTier, setUpgradeTier] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  // === Per-row delete ===
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // === Drive OAuth status — owner spec 2026-06-15g: surface a clear
  //     "Reconnect Drive" button at the TOP whenever needs_reauth=true,
  //     so the user doesn't need to hunt for it in settings.
  const [driveStatus, setDriveStatus] = useState(null);  // {connected, needs_reauth, configured}
  const [reconnecting, setReconnecting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4, r5] = await Promise.all([
        api.get(`/companies/${companyId}/smart-snapshots`),
        api.get(`/companies/${companyId}/smart-snapshots/restore-history`),
        api.get("/companies").catch(() => ({ data: [] })),
        api.get(`/companies/${companyId}/smart-snapshots/quota`).catch(() => ({ data: null })),
        api.get("/gdrive/info").catch(() => ({ data: null })),
      ]);
      setSnapshots(r1.data?.snapshots || []);
      setHistory(r2.data?.history || []);
      const items = Array.isArray(r3.data) ? r3.data : (r3.data.items || r3.data.companies || []);
      const comp = items.find((c) => c.id === companyId) || {};
      setFolderUrl(comp.gdrive_folder_url || "");
      setSavedUrl(comp.gdrive_folder_url || "");
      setQuota(r4.data);
      setDriveStatus(r5.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "טעינה נכשלה");
    } finally { setLoading(false); }
  };

  const onReconnectDrive = async () => {
    setReconnecting(true);
    try {
      const { data } = await api.get("/gdrive/oauth/start");
      if (data?.url) {
        // Open Google consent in a new tab. After the callback the
        // backend updates drive_oauth_credentials and clears needs_reauth.
        window.open(data.url, "_blank", "noopener,noreferrer");
        toast.info("נפתח חלון Google — אשר את ההרשאה ואז חזור לכאן ולחץ 'רענן'");
      } else {
        toast.error("לא ניתן להתחיל חיבור Drive — בדוק את ההגדרות");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בהתחלת החיבור");
    } finally { setReconnecting(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  const onSaveDriveFolder = async () => {
    setSavingDrive(true);
    try {
      await api.patch(`/companies/${companyId}`, { gdrive_folder_url: folderUrl.trim() });
      setSavedUrl(folderUrl.trim());
      toast.success(folderUrl.trim() ? "✓ תיקיית Drive נשמרה — גיבויים הבאים יעלו לשם" : "הקישור נמחק");
    } catch (e) {
      toast.error(e.response?.data?.detail || "שמירה נכשלה");
    } finally { setSavingDrive(false); }
  };

  // Build a lookup of YYYY-MM-DD → [snapshots] (all snaps for that day).
  const snapByDate = useMemo(() => {
    const m = {};
    for (const s of snapshots) {
      const d = s.date || (s.created_at || "").slice(0, 10);
      if (!m[d]) m[d] = [];
      m[d].push(s);
    }
    // Sort each day's bucket newest first.
    for (const d of Object.keys(m)) {
      m[d].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return m;
  }, [snapshots]);
  // Currently-selected snapshot within the selected day (defaults to newest).
  const [selectedSnapId, setSelectedSnapId] = useState(null);

  const monthGrid = useMemo(() => {
    const first = new Date(cursor.year, cursor.month, 1);
    const lastDay = new Date(cursor.year, cursor.month + 1, 0).getDate();
    const startWeekday = first.getDay(); // 0=Sunday
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= lastDay; d++) {
      const iso = `${cursor.year}-${String(cursor.month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const list = snapByDate[iso] || [];
      cells.push({ day: d, iso, snaps: list, snap: list[0] || null });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor, snapByDate]);

  const prevMonth = () => setCursor((c) =>
    c.month === 0 ? { year: c.year - 1, month: 11 } : { ...c, month: c.month - 1 });
  const nextMonth = () => setCursor((c) =>
    c.month === 11 ? { year: c.year + 1, month: 0 } : { ...c, month: c.month + 1 });

  const lastRestore = history[0];
  const canUndo = lastRestore && lastRestore.pre_restore_snapshot_id && !lastRestore.undone_at;
  // canRedo: any restore entry that was undone (and therefore eligible to be
  // re-applied). We pick the most-recently-undone one as the redo target.
  const lastUndone = (history || []).find((h) => h.undone_at);
  const canRedo = !!lastUndone;

  const onManualSnapshot = async (opts = {}) => {
    setCreating(true);
    try {
      const url = opts.policy
        ? `/companies/${companyId}/smart-snapshots/create-now?policy=${opts.policy}`
        : `/companies/${companyId}/smart-snapshots/create-now`;
      const { data } = await api.post(url);
      const snap = data?.snapshot || {};
      const driveOk = !!snap.gdrive_link;
      const driveErr = snap.gdrive_error;
      if (data?.reason === "quota_exceeded") {
        // Server says we'd cross the 200MB cap. Show the upgrade/overwrite
        // popup; the user picks the next action.
        setQuotaPopup({
          would_add_mb: data.would_add_mb,
          quota: data.quota,
        });
        return;
      }
      if (data?.created) {
        if (driveOk) {
          toast.success("גיבוי נוצר והועלה ל-Drive ✓", {
            description: snap.gdrive_link,
            duration: 6000,
          });
        } else if (driveErr === "no_gdrive_folder_configured") {
          toast.warning("גיבוי נוצר — אבל לא הוגדרה תיקיית Drive", {
            description: "הזן URL של תיקיית Drive בהגדרות החברה כדי להעלות אוטומטית.",
            duration: 8000,
          });
        } else if (driveErr) {
          // Detect the most common Google OAuth re-auth case so the SA
          // sees actionable wording (and a "Reconnect Drive" CTA via
          // the parent screen).
          const needsReauth = /invalid_grant|invalid_token|unauthorized_client/i.test(driveErr);
          toast.warning(
            needsReauth
              ? "חיבור Google Drive פג תוקף — יש לחבר מחדש"
              : "גיבוי נוצר — העלאה ל-Drive נכשלה",
            {
              description: needsReauth
                ? "פתח את הגדרות החברה ולחץ 'חבר Drive מחדש' (החיבור הקודם בוטל ע״י Google — קורה בד״כ לאחר שינוי סיסמה או היעדר שימוש)."
                : driveErr.slice(0, 120),
              duration: 10000,
            },
          );
        } else {
          toast.success("גיבוי נוצר בהצלחה");
        }
      } else if (data?.reason === "no_changes_since_last_snapshot") {
        toast("אין שינויים — לא נוצר גיבוי חדש", { icon: "ℹ️" });
      } else if (data?.reason === "snapshot_for_today_already_exists") {
        toast("כבר יש גיבוי להיום", { icon: "ℹ️" });
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "יצירה נכשלה");
    } finally { setCreating(false); }
  };

  const onQuotaOverwrite = async () => {
    // 1. Save the preference so future backups (especially the daily
    //    cron, which has no user to ask) don't pop the modal again.
    try {
      await api.post(
        `/companies/${companyId}/smart-snapshots/set-policy`,
        { policy: "overwrite_oldest" },
      );
    } catch { /* non-fatal — the immediate retry below still works */ }
    setQuotaPopup(null);
    await onManualSnapshot({ policy: "overwrite_oldest" });
    load();
  };
  const onQuotaUpgrade = () => {
    setQuotaPopup(null);
    setUpgradePopup(true);
    setUpgradeTier(null);
  };
  const onConfirmUpgrade = async () => {
    if (!upgradeTier) return;
    setUpgrading(true);
    try {
      const { data } = await api.post(
        `/companies/${companyId}/smart-snapshots/upgrade-quota`,
        { new_quota_mb: upgradeTier.mb },
      );
      const pr = data?.prorata || {};
      const delta = pr.prorata_delta_ils ?? 0;
      const msg = delta > 0
        ? `שודרגת ל-${upgradeTier.label_mb} · יחיובת ${delta} ₪ (יחסי, ${pr.days_remaining} ימים נותרו בחודש)`
        : delta < 0
          ? `הורדת ל-${upgradeTier.label_mb} · יזוכה לך ${Math.abs(delta)} ₪ (יחסי)`
          : `החבילה עודכנה ל-${upgradeTier.label_mb}`;
      toast.success(msg, { duration: 7000 });
      setUpgradePopup(false);
      setUpgradeTier(null);
      setProrataPreview(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שדרוג נכשל");
    } finally { setUpgrading(false); }
  };

  // === Pro-rata preview ============================================
  // When the user picks a tier in the upgrade modal we fetch the
  // prorated charge/refund from the server so they see EXACTLY what
  // they'll pay/receive before clicking שדרג.
  const [prorataPreview, setProrataPreview] = useState(null);
  const [prorataLoading, setProrataLoading] = useState(false);
  useEffect(() => {
    if (!upgradeTier || !upgradePopup) { setProrataPreview(null); return; }
    setProrataLoading(true);
    api.get(`/companies/${companyId}/smart-snapshots/prorata?new_quota_mb=${upgradeTier.mb}`)
      .then(({ data }) => setProrataPreview(data))
      .catch(() => setProrataPreview(null))
      .finally(() => setProrataLoading(false));
  }, [upgradeTier, upgradePopup, companyId]);

  const onConfirmDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/companies/${companyId}/smart-snapshots/${confirmDelete.id}`);
      toast.success("הגיבוי נמחק");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "מחיקה נכשלה");
    } finally { setDeleting(false); }
  };

  const onConfirmRestore = async () => {
    if (!confirmRestore) return;
    setRestoring(true);
    setPostReport(null);
    try {
      const { data } = await api.post(
        `/companies/${companyId}/smart-snapshots/${confirmRestore.id}/restore`,
      );
      toast.success(`שוחזר ל-${confirmRestore.date}`);
      setPostReport(data);
      setConfirmRestore(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שחזור נכשל");
    } finally { setRestoring(false); }
  };

  // === File-upload restore flow ===
  const onPickUploadFile = () => fileInputRef.current?.click();
  const onUploadFileChosen = (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = ""; // allow re-picking the same file
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("יש לבחור קובץ .zip");
      return;
    }
    setConfirmUpload({ file });
  };
  const onConfirmUpload = async () => {
    if (!confirmUpload?.file) return;
    setUploading(true);
    setPostReport(null);
    try {
      const form = new FormData();
      form.append("file", confirmUpload.file);
      const { data } = await api.post(
        `/companies/${companyId}/smart-snapshots/restore-from-upload`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(`שוחזר מהקובץ — ${confirmUpload.file.name}`, { duration: 5000 });
      setPostReport(data);
      setConfirmUpload(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שחזור מקובץ נכשל");
    } finally { setUploading(false); }
  };

  const onUndo = async () => {
    if (!canUndo) return;
    if (!window.confirm(`לבטל את השחזור האחרון? המערכת תחזור למצב שלפניו.`)) return;
    setRestoring(true);
    setPostReport(null);
    try {
      const { data } = await api.post(
        `/companies/${companyId}/smart-snapshots/undo-last-restore`,
      );
      toast.success("השחזור בוטל");
      setPostReport(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "ביטול נכשל");
    } finally { setRestoring(false); }
  };

  const onRedo = async () => {
    if (!canRedo) return;
    if (!window.confirm(`להחזיר את השחזור שבוטל?`)) return;
    setRestoring(true);
    setPostReport(null);
    try {
      const { data } = await api.post(
        `/companies/${companyId}/smart-snapshots/redo-last-undo`,
      );
      toast.success("השחזור הוחזר");
      setPostReport(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "החזרה נכשלה");
    } finally { setRestoring(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[10100] bg-black/60 flex items-center justify-center p-3"
      onClick={onClose}
      data-testid="smart-backup-modal"
    >
      <div
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
        className="bg-stone-950 rounded-2xl border border-emerald-700/40 shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
      >
        {/* Header — RTL aware. Mobile: title row stays at the top with
            the X close button pinned to the top-right; action buttons
            wrap below, with the orange "upload" button flush-left and
            the undo/redo arrows on a row of their own underneath. */}
        <header className="relative px-4 sm:px-5 py-3 bg-gradient-to-l from-emerald-900/40 to-stone-900 border-b border-stone-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          {/* Close (X) — absolute top-right on mobile so it never wraps */}
          <button
            type="button"
            onClick={onClose}
            className="absolute top-2 left-2 sm:static h-8 w-8 rounded-md text-stone-400 hover:bg-white/5 sm:order-last shrink-0 z-10"
            data-testid="smart-backup-close"
            title="סגור"
          >
            <X className="w-4 h-4 mx-auto" />
          </button>

          <div className="flex items-center gap-2 pe-10 sm:pe-0">
            <Calendar className="w-5 h-5 text-emerald-300" />
            <div>
              <h2 className="text-base font-extrabold text-white">גיבויים חכמים</h2>
              <p className="text-[11px] text-stone-400">לוח שנה • Disaster Recovery • 30 ימים אחורה</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-start sm:justify-end">
            <button
              type="button"
              onClick={onManualSnapshot}
              disabled={creating}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12px] font-extrabold bg-emerald-600 hover:bg-emerald-500 text-white border border-emerald-400/40 shadow-lg shadow-emerald-900/30 disabled:opacity-50 transition"
              data-testid="smart-snapshot-create-now"
              title="גיבוי ידני — נוצר מיד ומועלה ל-Google Drive"
            >
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Cloud className="w-3.5 h-3.5" />}
              {creating ? "מגבה ל-Drive..." : "צור גיבוי ל-Drive"}
            </button>

            {/* Always-visible "download manual backup" button — even
                when no day is selected, this downloads the MOST RECENT
                snapshot. Built-in, no day-click required. */}
            {(() => {
              const newest = snapshots.find((s) => !s.deleted_at);
              const targetId = (selectedDate && (snapByDate[selectedDate]?.find((s) => s.id === selectedSnapId) || snapByDate[selectedDate]?.[0]))?.id
                              || newest?.id;
              const disabled = !targetId;
              return (
                <a
                  href={targetId ? `${process.env.REACT_APP_BACKEND_URL}/api/companies/${companyId}/smart-snapshots/${targetId}/download` : "#"}
                  download
                  onClick={(e) => { if (disabled) { e.preventDefault(); toast("אין גיבוי זמין להורדה"); } }}
                  className={"inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-bold bg-stone-800 hover:bg-stone-700 border border-stone-700 text-stone-200 " + (disabled ? "opacity-40 cursor-not-allowed" : "")}
                  data-testid="smart-snapshot-download-top"
                  title={selectedDate ? "הורד את הגיבוי הנבחר אל המחשב" : "הורד את הגיבוי האחרון אל המחשב"}
                >
                  <Download className="w-3.5 h-3.5" />
                  הורד גיבוי ידני למחשב
                </a>
              );
            })()}

            {/* Upload ZIP from the user's computer → server takes a
                safety snapshot then restores. On mobile, this button
                is pushed flush-LEFT via `me-auto` (RTL: ms-auto pushes
                to the visual left). */}
            <button
              type="button"
              onClick={onPickUploadFile}
              disabled={uploading}
              className="me-auto sm:me-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-bold bg-amber-600 hover:bg-amber-500 text-white border border-amber-400/40 disabled:opacity-50"
              data-testid="smart-snapshot-upload-restore"
              title="שחזור מקובץ ZIP מהמחשב"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading ? "משחזר מקובץ..." : "העלה גיבוי מהמחשב"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip,application/zip"
              onChange={onUploadFileChosen}
              className="hidden"
              data-testid="smart-snapshot-file-input"
            />

            {/* Undo / Redo arrows — on mobile they wrap onto their own
                line BELOW the orange button (basis-full forces wrap),
                on desktop they stay inline on the same row. */}
            <div
              className="basis-full sm:basis-auto inline-flex items-center gap-1 justify-end sm:justify-start"
              data-testid="undo-redo-controls"
            >
              <button
                type="button"
                onClick={onUndo}
                disabled={restoring || !canUndo}
                className="h-9 w-9 rounded-full bg-stone-800 hover:bg-amber-600/30 border border-stone-700 hover:border-amber-500/60 text-stone-300 hover:text-amber-200 transition disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center justify-center"
                data-testid="smart-snapshot-undo"
                title={canUndo ? `בטל שחזור (מ-${lastRestore?.snapshot_date})` : "אין שחזור לבטל"}
              >
                <Undo2 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={onRedo}
                disabled={restoring || !canRedo}
                className="h-9 w-9 rounded-full bg-stone-800 hover:bg-emerald-600/30 border border-stone-700 hover:border-emerald-500/60 text-stone-300 hover:text-emerald-200 transition disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center justify-center"
                data-testid="smart-snapshot-redo"
                title={canRedo ? `החזר שחזור שבוטל (ל-${lastUndone?.snapshot_date})` : "אין שחזור להחזיר"}
              >
                <Undo2 className="w-4 h-4 scale-x-[-1]" />
              </button>
            </div>
          </div>
        </header>

        {/* Quota usage bar — visible always, color-coded.
            Green ≤ 60%, Amber 60-90%, Red > 90%.
            Also hosts the "שינוי חבילת הגיבוי" button on the left so
            the user can change plans without scrolling away. */}
        {quota && (
          <div className="px-5 py-2 border-b border-stone-800 bg-stone-950/40 flex items-center gap-3" data-testid="quota-bar">
            <button
              type="button"
              onClick={() => { setUpgradePopup(true); setUpgradeTier(null); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-fuchsia-600/30 hover:bg-fuchsia-600/50 border border-fuchsia-500/40 text-fuchsia-200 flex-shrink-0"
              data-testid="smart-snapshot-change-plan"
              title="שדרג/הורד את חבילת הגיבוי — עם חישוב יחסי"
            >
              <HardDrive className="w-3.5 h-3.5" />
              שינוי חבילת הגיבוי
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-bold text-stone-300 inline-flex items-center gap-1.5">
                  <HardDrive className="w-3.5 h-3.5 text-stone-400" />
                  שטח גיבוי בשימוש
                </span>
                <span className="text-[11px] tabular-nums text-stone-300">
                  <strong className={quota.percent > 90 ? "text-red-300" : quota.percent > 60 ? "text-amber-300" : "text-emerald-300"}>
                    {quota.used_mb.toFixed(1)}MB
                  </strong>
                  {" / "}{quota.quota_mb}MB · <span className="text-stone-500">{quota.percent}%</span>
                </span>
              </div>
              <div className="w-full h-1.5 rounded-full bg-stone-900 overflow-hidden">
                <div
                  className={`h-full transition-all ${quota.percent > 90 ? "bg-red-500" : quota.percent > 60 ? "bg-amber-500" : "bg-emerald-500"}`}
                  style={{ width: `${Math.min(100, quota.percent)}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Drive folder URL — inline config so the user can paste it
            without leaving the calendar. Always visible, sticky-ish.
            Owner spec 2026-06-15g: when the Drive OAuth refresh-token
            is revoked (needs_reauth), surface a BIG RED reconnect button
            ABOVE the folder input so the SA can't miss it. */}
        {driveStatus?.needs_reauth && (
          <div className="px-5 py-3 border-b border-rose-500/40 bg-rose-950/60" data-testid="calendar-gdrive-reauth-banner">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-300 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-extrabold text-rose-200">חיבור Google Drive פג תוקף</div>
                <div className="text-[11px] text-rose-300/80">לחץ "חבר Drive מחדש" ואשר את ההרשאה ב-Google. לאחר מכן הגיבוי האוטומטי יחזור לעבוד.</div>
              </div>
              <button
                type="button"
                onClick={onReconnectDrive}
                disabled={reconnecting}
                className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-l from-rose-600 to-rose-500 hover:from-rose-500 hover:to-rose-400 text-white font-extrabold text-xs sm:text-sm shadow-lg shadow-rose-500/30 disabled:opacity-60"
                data-testid="calendar-gdrive-reconnect"
              >
                {reconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                חבר Drive מחדש
              </button>
            </div>
          </div>
        )}

        <div className="px-5 py-3 border-b border-stone-800 bg-stone-950/60" data-testid="calendar-gdrive-config">
          <div className="flex items-center gap-2 mb-1.5">
            <Cloud className="w-3.5 h-3.5 text-sky-300" />
            <span className="text-[11px] font-extrabold text-sky-200">תיקיית Google Drive לגיבויים</span>
            {savedUrl && !driveStatus?.needs_reauth && (
              <span className="text-[9px] font-bold text-emerald-300 bg-emerald-500/20 border border-emerald-500/40 rounded-full px-1.5 py-0.5">
                פעיל
              </span>
            )}
            {savedUrl && driveStatus?.needs_reauth && (
              <span className="text-[9px] font-bold text-rose-300 bg-rose-500/20 border border-rose-500/40 rounded-full px-1.5 py-0.5">
                פג תוקף
              </span>
            )}
            {!savedUrl && (
              <span className="text-[9px] font-bold text-amber-300 bg-amber-500/20 border border-amber-500/40 rounded-full px-1.5 py-0.5">
                לא מוגדר — Drive לא יקבל גיבויים
              </span>
            )}
          </div>
          <div className="flex gap-1.5">
            <input
              type="text"
              value={folderUrl}
              onChange={(e) => setFolderUrl(e.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
              dir="ltr"
              className="flex-1 text-right bg-stone-900 border border-stone-700 rounded-lg px-3 py-1.5 text-sky-100 placeholder:text-stone-600 text-[12px] focus:outline-none focus:border-sky-500/60"
              data-testid="calendar-gdrive-folder-url"
            />
            <button
              type="button"
              onClick={onSaveDriveFolder}
              disabled={savingDrive || folderUrl.trim() === savedUrl.trim()}
              className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-bold text-[12px] disabled:opacity-50"
              data-testid="calendar-gdrive-save"
            >
              {savingDrive ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              שמור
            </button>
            <button
              type="button"
              onClick={onReconnectDrive}
              disabled={reconnecting}
              className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-white font-bold text-[12px] disabled:opacity-50"
              title="פתח את חלון Google כדי לאשר את הרשאת Drive מחדש"
              data-testid="calendar-gdrive-reconnect-inline"
            >
              {reconnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              חבר Drive
            </button>
            {savedUrl && (
              <a
                href={savedUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center px-2 py-1.5 rounded-lg bg-stone-800 hover:bg-stone-700 text-sky-300 hover:text-sky-200 border border-stone-700"
                title="פתח תיקייה ב-Drive"
                data-testid="calendar-gdrive-open"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-3 gap-4 p-5">
          {/* Calendar (2/3) */}
          <section className="md:col-span-2 bg-stone-900/60 rounded-xl border border-stone-800 p-4">
            <div className="flex items-center justify-between mb-3">
              <button type="button" onClick={nextMonth} className="text-stone-300 hover:text-white p-1 rounded" data-testid="cal-next">
                <ChevronRight className="w-4 h-4" />
              </button>
              <div className="text-sm font-extrabold text-white">
                {HE_MONTHS[cursor.month]} {cursor.year}
              </div>
              <button type="button" onClick={prevMonth} className="text-stone-300 hover:text-white p-1 rounded" data-testid="cal-prev">
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 text-[11px] text-stone-400 mb-1 text-center font-bold">
              {HE_DAYS.map((d) => <div key={d}>{d}</div>)}
            </div>

            {loading ? (
              <div className="h-72 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-stone-500" /></div>
            ) : (
              <div className="grid grid-cols-7 gap-1" data-testid="smart-calendar-grid">
                {monthGrid.map((cell, i) => {
                  if (!cell) return <div key={i} className="aspect-square" />;
                  const hasSnap = !!cell.snap;
                  const snapCount = cell.snaps?.length || 0;
                  const isSel = selectedDate === cell.iso;
                  return (
                    <button
                      key={cell.iso}
                      type="button"
                      disabled={!hasSnap}
                      onClick={() => { setSelectedDate(cell.iso); setSelectedSnapId(cell.snap?.id || null); }}
                      data-testid={hasSnap ? `cal-day-${cell.iso}` : undefined}
                      className={
                        "aspect-square rounded-lg flex flex-col items-center justify-center text-[12px] font-bold border transition relative " + (
                          hasSnap
                            ? (isSel
                                ? "bg-emerald-600 text-white border-emerald-400 shadow-lg"
                                : "bg-emerald-700/25 text-emerald-200 border-emerald-700/40 hover:bg-emerald-600/40")
                            : "bg-stone-900/40 text-stone-600 border-stone-800 cursor-not-allowed opacity-60"
                        )
                      }
                    >
                      <span>{cell.day}</span>
                      {hasSnap && (
                        <span className="text-[8px] mt-0.5 opacity-80">
                          {snapCount > 1
                            ? `${snapCount} גיבויים`
                            : (cell.snap.trigger === "manual" ? "ידני" :
                               cell.snap.trigger === "pre_restore" ? "אוטומטי" : "אוטומטי")}
                        </span>
                      )}
                      {snapCount > 1 && (
                        <span className="absolute top-0.5 left-0.5 text-[9px] font-extrabold bg-amber-500 text-stone-900 rounded-full px-1 leading-none py-0.5 min-w-[14px]">
                          {snapCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="mt-3 flex items-center gap-3 text-[10px] text-stone-400">
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-emerald-500"></span> גיבוי קיים
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-stone-700"></span> אין שינוי / לא נוצר
              </span>
            </div>
          </section>

          {/* Day detail (1/3) */}
          <section className="bg-stone-900/60 rounded-xl border border-stone-800 p-4 min-h-[420px]">
            {!selectedDate ? (
              <div className="h-full flex flex-col items-center justify-center text-stone-500 text-center text-[12px]">
                <Calendar className="w-10 h-10 mb-2 opacity-40" />
                בחר תאריך כדי לראות את הגיבוי
              </div>
            ) : (() => {
              const daySnaps = snapByDate[selectedDate] || [];
              if (daySnaps.length === 0) return <div className="text-stone-500">אין נתון</div>;
              // Pick the currently-selected snap (by id) within the day,
              // falling back to the newest.
              const snap = daySnaps.find((s) => s.id === selectedSnapId) || daySnaps[0];
              const colls = Object.entries(snap.counts || {}).filter(([k]) => k !== "__assets__");
              const totalRecords = colls.reduce((a, [, n]) => a + n, 0);
              const assets = (snap.counts || {}).__assets__ || 0;
              return (
                <div className="space-y-3" data-testid={`smart-day-${selectedDate}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold">{selectedDate}</div>
                    <span className="text-[10px] font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5">
                      {daySnaps.length} {daySnaps.length === 1 ? "גיבוי" : "גיבויים"}
                    </span>
                  </div>

                  {/* Time-ordered list of snapshots for this day. Each row
                      is clickable to pick it as the currently-detailed
                      snapshot. Shown even when there's only one, to keep
                      the layout consistent. */}
                  <div className="rounded-lg border border-stone-800 bg-stone-950 max-h-44 overflow-y-auto" data-testid={`smart-day-list-${selectedDate}`}>
                    {daySnaps.map((s) => {
                      const active = s.id === snap.id;
                      const time = new Date(s.created_at).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
                      return (
                        <div
                          key={s.id}
                          className={"flex items-center gap-1.5 px-2 py-1.5 border-b border-stone-800 last:border-b-0 transition " +
                            (active ? "bg-emerald-700/20" : "hover:bg-stone-900")}
                          data-testid={`smart-day-snap-${s.id}`}
                        >
                          <button
                            type="button"
                            onClick={() => setSelectedSnapId(s.id)}
                            className="flex-1 flex items-center gap-2 text-right min-w-0"
                            data-testid={`smart-day-snap-select-${s.id}`}
                          >
                            <span className="text-[11px] text-stone-300 font-bold tabular-nums w-16">{time}</span>
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                              s.trigger === "manual" ? "bg-fuchsia-500/20 text-fuchsia-200" :
                              s.trigger === "pre_restore" ? "bg-amber-500/20 text-amber-200" :
                              s.trigger === "imported" ? "bg-sky-500/20 text-sky-200" :
                              "bg-emerald-500/20 text-emerald-200"}`}>
                              {s.trigger === "manual" ? "ידני" :
                               s.trigger === "pre_restore" ? "אוטומטי" :
                               s.trigger === "imported" ? "מקובץ" : "אוטומטי"}
                            </span>
                            <span className="text-[10px] text-stone-400 flex-1 truncate">{_bytes(s.file_size)}</span>
                            {s.gdrive_link && <Cloud className="w-3 h-3 text-sky-300 flex-shrink-0" />}
                          </button>
                          {/* Per-row restore button — opens the SAME
                              confirmation popup as the big CTA. */}
                          <button
                            type="button"
                            onClick={() => setConfirmRestore(s)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-600/80 hover:bg-red-500 text-white text-[10px] font-extrabold flex-shrink-0"
                            data-testid={`smart-day-snap-restore-${s.id}`}
                            title={`שחזר מ-${time}`}
                          >
                            <RotateCcw className="w-3 h-3" /> שחזר
                          </button>
                          {/* Per-row trash icon — soft-delete with
                              "האם בטוח?" confirmation popup. */}
                          <button
                            type="button"
                            onClick={() => setConfirmDelete(s)}
                            className="inline-flex items-center justify-center w-6 h-6 rounded-md text-stone-400 hover:text-red-300 hover:bg-red-500/10 transition flex-shrink-0"
                            data-testid={`smart-day-snap-delete-${s.id}`}
                            title="מחק את הגיבוי הזה"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-[12px]">
                    <div className="bg-stone-950 rounded p-2 border border-stone-800">
                      <div className="text-[10px] text-stone-500 mb-0.5">שעה</div>
                      <div className="text-white font-bold">{_fmtTime(snap.created_at)}</div>
                    </div>
                    <div className="bg-stone-950 rounded p-2 border border-stone-800">
                      <div className="text-[10px] text-stone-500 mb-0.5">גודל</div>
                      <div className="text-white font-bold">{_bytes(snap.file_size)}</div>
                    </div>
                    <div className="bg-stone-950 rounded p-2 border border-stone-800">
                      <div className="text-[10px] text-stone-500 mb-0.5 flex items-center gap-1"><Database className="w-2.5 h-2.5" /> Records</div>
                      <div className="text-white font-bold">{totalRecords.toLocaleString("he-IL")}</div>
                    </div>
                    <div className="bg-stone-950 rounded p-2 border border-stone-800">
                      <div className="text-[10px] text-stone-500 mb-0.5 flex items-center gap-1"><HardDrive className="w-2.5 h-2.5" /> Assets</div>
                      <div className="text-white font-bold">{assets}</div>
                    </div>
                  </div>

                  <div className="text-[10px] text-stone-500 break-all">
                    fingerprint: <span className="text-stone-300">{snap.fingerprint?.slice(0, 24)}…</span>
                  </div>

                  <div className="bg-stone-950 rounded border border-stone-800 max-h-44 overflow-y-auto p-2">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1">קולקציות</div>
                    <ul className="text-[11px] text-stone-300 space-y-0.5">
                      {colls.length === 0 && <li className="text-stone-500">—</li>}
                      {colls.map(([k, n]) => (
                        <li key={k} className="flex justify-between">
                          <span className="truncate">{k}</span>
                          <span className="tabular-nums text-stone-400">{n.toLocaleString("he-IL")}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {snap.last_restored_at && (
                    <div className="text-[10px] text-amber-300 bg-amber-900/30 rounded border border-amber-700/40 px-2 py-1">
                      🔄 כבר שוחזר: {new Date(snap.last_restored_at).toLocaleString("he-IL")}
                    </div>
                  )}

                  {/* Drive upload status — the small per-snapshot Drive
                      link/warning was removed per owner request because
                      the Drive URL is already shown in the top banner.
                      Errors that aren't "no folder" still surface in the
                      day-list row icon (cloud icon when uploaded, warning
                      tooltip otherwise). */}
                </div>
              );
            })()}
          </section>
        </div>

        {/* Footer with "last restores" removed per owner request — info
            still available via Undo button visibility. */}

        {/* === Delete confirmation popup =================== */}
        {confirmDelete && (
          <div className="fixed inset-0 z-[10150] bg-black/70 flex items-center justify-center p-4" onClick={() => !deleting && setConfirmDelete(null)} data-testid="delete-confirm-modal">
            <div dir="rtl" onClick={(e) => e.stopPropagation()} className="bg-stone-900 rounded-2xl border border-red-600/40 max-w-md w-full p-5 space-y-3">
              <div className="flex items-center gap-2 text-red-300">
                <Trash2 className="w-5 h-5" />
                <h3 className="text-base font-extrabold">האם בטוח למחוק?</h3>
              </div>
              <p className="text-sm text-stone-300 leading-relaxed">
                למחוק את הגיבוי מ-<strong className="text-red-200">
                  {confirmDelete.date} {new Date(confirmDelete.created_at).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </strong>?
              </p>
              <ul className="text-[11px] text-stone-400 list-disc pe-4 space-y-0.5">
                <li>הקובץ ({_bytes(confirmDelete.file_size)}) יימחק מהדיסק לצמיתות</li>
                <li>שטח האחסון יתפנה במכסה שלך</li>
                <li>הרשומה תישאר בלוג של מנהל המערכת לצורך מעקב</li>
              </ul>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setConfirmDelete(null)} disabled={deleting}
                  className="px-3 py-1.5 rounded text-[12px] font-bold text-stone-300 hover:bg-stone-800"
                  data-testid="smart-delete-cancel"
                >לא</button>
                <button type="button" onClick={onConfirmDelete} disabled={deleting}
                  className="inline-flex items-center gap-1 px-4 py-1.5 rounded text-[12px] font-extrabold bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
                  data-testid="smart-delete-confirm"
                >
                  {deleting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  כן, מחק
                </button>
              </div>
            </div>
          </div>
        )}

        {/* === Quota-exceeded popup ============================
            Shown when the server returns `quota_exceeded`. The user
            has 2 options: auto-overwrite oldest, or upgrade.
        */}
        {quotaPopup && (
          <div className="fixed inset-0 z-[10130] bg-black/70 flex items-center justify-center p-4" onClick={() => setQuotaPopup(null)} data-testid="quota-exceeded-modal">
            <div dir="rtl" onClick={(e) => e.stopPropagation()} className="bg-stone-900 rounded-2xl border border-amber-500/40 max-w-md w-full p-5 space-y-4">
              <div className="flex items-center gap-2 text-amber-300">
                <AlertTriangle className="w-5 h-5" />
                <h3 className="text-base font-extrabold">עברת את מכסת הגיבוי</h3>
              </div>
              <p className="text-sm text-stone-300 leading-relaxed">
                גיבוי חדש ידרוש <strong className="text-amber-200">{quotaPopup.would_add_mb}MB</strong>,
                אבל יש לך רק <strong className="text-amber-200">{(quotaPopup.quota.quota_mb - quotaPopup.quota.used_mb).toFixed(1)}MB</strong> פנויים מתוך{" "}
                <strong>{quotaPopup.quota.quota_mb}MB</strong> בחבילה.
              </p>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={onQuotaOverwrite}
                  disabled={creating}
                  className="w-full text-right rounded-xl border border-stone-700 bg-stone-800 hover:bg-stone-700 p-3 transition disabled:opacity-50"
                  data-testid="quota-option-overwrite"
                >
                  <div className="font-extrabold text-white text-sm">1. דרוס את הגיבוי הישן ביותר</div>
                  <div className="text-[11px] text-stone-400 mt-0.5">
                    המערכת תמחק אוטומטית את הגיבויים הישנים ביותר עד שיתפנה מקום.
                  </div>
                  <div className="text-[10px] text-emerald-300 mt-1 font-bold">
                    ✓ הבחירה תישמר — גיבויים אוטומטיים יומיים יעשו זאת לבד מעכשיו ואילך, בלי לשאול שוב.
                  </div>
                </button>
                <button
                  type="button"
                  onClick={onQuotaUpgrade}
                  className="w-full text-right rounded-xl border border-sky-500/40 bg-sky-900/20 hover:bg-sky-900/40 p-3 transition"
                  data-testid="quota-option-upgrade"
                >
                  <div className="font-extrabold text-sky-200 text-sm">2. לרכוש מקום נוסף — עד 3GB</div>
                  <div className="text-[11px] text-sky-400/80 mt-0.5">
                    הוסף 500MB בכל פעם (<strong className="text-sky-200">10 קרדיטים</strong> לחודש), עד מקסימום 3GB. הגיבויים שלך יישמרו 5 שנים.
                  </div>
                </button>
              </div>
              <div className="flex justify-end">
                <button type="button" onClick={() => setQuotaPopup(null)}
                  className="px-3 py-1 rounded text-[12px] text-stone-400 hover:bg-stone-800"
                  data-testid="quota-popup-close"
                >ביטול</button>
              </div>
            </div>
          </div>
        )}

        {/* === Upgrade-tier popup ============================
            Shown after the user clicks "לרכוש מקום נוסף". Lists
            tiers (500MB → 5.9₪, up to 3GB).
        */}
        {upgradePopup && (
          <div className="fixed inset-0 z-[10140] bg-black/70 flex items-center justify-center p-4" onClick={() => !upgrading && setUpgradePopup(false)} data-testid="upgrade-tier-modal">
            <div dir="rtl" onClick={(e) => e.stopPropagation()} className="bg-stone-900 rounded-2xl border border-sky-500/40 max-w-lg w-full p-5 space-y-4">
              <div className="flex items-center gap-2 text-sky-300">
                <Cloud className="w-5 h-5" />
                <h3 className="text-base font-extrabold">שינוי חבילת הגיבוי</h3>
              </div>
              <p className="text-[12px] text-stone-300 leading-relaxed">
                בחר/י את החבילה הרצויה. שדרוג מחייב יחסית (Pro-rata) לפי הימים שנותרו בחודש,
                והורדה מזכה אותך בהפרש. החיוב חודשי וניתן לבטל בכל עת. מקסימום: 3GB.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(() => {
                  // Tiers 500MB → 3000MB in steps of 500
                  const base = quota?.default_quota_mb || 200;
                  const tierMb = quota?.tier_mb || 500;
                  const price = quota?.tier_price_ils || 5.9;
                  const tiers = [];
                  // First option: BASE plan (free, used for downgrade).
                  tiers.push({
                    mb: base,
                    label_mb: `${base}MB`,
                    extra_mb: 0,
                    price_ils: "0.00",
                  });
                  let total = base + tierMb;
                  while (total <= 3000) {
                    const stepsAdded = Math.round((total - base) / tierMb);
                    tiers.push({
                      mb: total,
                      label_mb: total >= 1000 ? `${(total/1000).toFixed(total % 1000 === 0 ? 1 : 2)}GB` : `${total}MB`,
                      extra_mb: total - base,
                      price_ils: (price * stepsAdded).toFixed(2),
                    });
                    total += tierMb;
                  }
                  return tiers.map((t) => {
                    const active = upgradeTier?.mb === t.mb;
                    const isCurrent = quota?.quota_mb === t.mb;
                    return (
                      <button
                        key={t.mb}
                        type="button"
                        onClick={() => !isCurrent && setUpgradeTier(t)}
                        disabled={isCurrent}
                        className={"text-right rounded-xl border p-3 transition " + (
                          isCurrent ? "border-stone-700 bg-stone-900 opacity-50 cursor-not-allowed" :
                          active ? "border-sky-400 bg-sky-700/30 ring-2 ring-sky-400/40" :
                          "border-stone-700 bg-stone-800 hover:bg-stone-700"
                        )}
                        data-testid={`upgrade-tier-${t.mb}`}
                      >
                        <div className="flex items-baseline justify-between">
                          <span className="text-xl font-extrabold text-white tabular-nums">{t.label_mb}</span>
                          <span className="text-[11px] text-stone-400">+{t.extra_mb}MB</span>
                        </div>
                        <div className="text-[13px] mt-1">
                          <strong className="text-emerald-300 tabular-nums">
                            {Number(t.price_ils) > 0 ? Math.round(Number(t.price_ils) / 0.6) : 0} קרדיטים
                          </strong>
                          <span className="text-stone-400"> / חודש</span>
                          {isCurrent && <span className="text-[10px] text-stone-500 ms-1">(החבילה הנוכחית)</span>}
                        </div>
                      </button>
                    );
                  });
                })()}
              </div>

              {/* Pro-rata calculation card — surfaces the EXACT
                  charge/refund the user will see, before they click
                  "שדרג". Updates each time they pick a tier. */}
              {upgradeTier && (
                <div className={"rounded-xl border p-3 space-y-2 " + (
                  prorataPreview?.is_upgrade ? "border-emerald-500/40 bg-emerald-500/5"
                  : prorataPreview?.is_downgrade ? "border-sky-500/40 bg-sky-500/5"
                  : "border-stone-700 bg-stone-900/60"
                )} data-testid="prorata-preview">
                  {prorataLoading ? (
                    <div className="flex items-center gap-2 text-stone-300 text-[12px]">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> מחשב חיוב יחסי...
                    </div>
                  ) : prorataPreview ? (
                    <>
                      <div className="flex items-center justify-between text-[12px]">
                        <span className="text-stone-400">מחיר חודשי נוכחי</span>
                        <span className="text-stone-200 tabular-nums font-bold">{prorataPreview.current_monthly_price} ₪</span>
                      </div>
                      <div className="flex items-center justify-between text-[12px]">
                        <span className="text-stone-400">מחיר חודשי חדש</span>
                        <span className="text-stone-200 tabular-nums font-bold">{prorataPreview.new_monthly_price} ₪</span>
                      </div>
                      <div className="flex items-center justify-between text-[12px]">
                        <span className="text-stone-400">ימים שנותרו בחודש</span>
                        <span className="text-stone-200 tabular-nums font-bold">{prorataPreview.days_remaining}</span>
                      </div>
                      <div className="h-px bg-stone-800 my-1" />
                      <div className="flex items-center justify-between">
                        <span className="font-extrabold text-white text-[13px]">
                          {prorataPreview.is_upgrade ? "תיחויב/י עכשיו (יחסי):"
                            : prorataPreview.is_downgrade ? "יזוכה לחשבונך (יחסי):"
                            : "ללא חיוב יחסי"}
                        </span>
                        <span className={"text-lg font-extrabold tabular-nums " + (
                          prorataPreview.is_upgrade ? "text-emerald-300"
                          : prorataPreview.is_downgrade ? "text-sky-300"
                          : "text-stone-300"
                        )}>
                          {prorataPreview.is_downgrade ? "−" : prorataPreview.is_upgrade ? "+" : ""}
                          {Math.abs(prorataPreview.prorata_delta_ils)} ₪
                          {Math.abs(prorataPreview.prorata_delta_ils) > 0 && (
                            <span className="text-[10px] text-stone-400 ms-1.5 font-bold">
                              ≈ {Math.round(Math.abs(prorataPreview.prorata_delta_ils) / 0.6)} קרדיטים
                            </span>
                          )}
                        </span>
                      </div>
                      <p className="text-[10px] text-stone-400 leading-snug">
                        החל מהחיוב הבא: <strong className="text-stone-200">{prorataPreview.new_monthly_price} ₪/חודש</strong>.
                        {prorataPreview.is_downgrade && " גיבויים מעבר למכסה החדשה ימחקו אוטומטית (החל מהישנים)."}
                      </p>
                    </>
                  ) : null}
                </div>
              )}
              <div className="text-[10px] text-amber-300/80 bg-amber-500/5 border border-amber-500/20 rounded px-2 py-1.5">
                ⚠️ דמו — חיוב לא מתבצע. שילוב Stripe יחובר בקרוב.
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setUpgradePopup(false)} disabled={upgrading}
                  className="px-3 py-1.5 rounded text-[12px] font-bold text-stone-300 hover:bg-stone-800"
                  data-testid="upgrade-cancel"
                >ביטול</button>
                <button type="button" onClick={onConfirmUpgrade} disabled={upgrading || !upgradeTier}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded text-[12px] font-extrabold bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-50"
                  data-testid="upgrade-confirm"
                >
                  {upgrading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {!upgradeTier ? "בחר חבילה"
                    : prorataPreview?.is_downgrade ? `הורד ל-${upgradeTier.label_mb}`
                    : prorataPreview?.is_upgrade ? `שדרג ל-${upgradeTier.label_mb}`
                    : `החל ${upgradeTier.label_mb}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upload-restore confirmation modal */}
        {confirmUpload && (
          <div className="fixed inset-0 z-[10110] bg-black/70 flex items-center justify-center p-4" onClick={() => !uploading && setConfirmUpload(null)}>
            <div dir="rtl" onClick={(e) => e.stopPropagation()} className="bg-stone-900 rounded-2xl border border-amber-600/40 max-w-md w-full p-5 space-y-3" data-testid="upload-restore-confirm-modal">
              <div className="flex items-center gap-2 text-amber-300">
                <Upload className="w-5 h-5" />
                <h3 className="text-base font-extrabold">שחזור מקובץ במחשב</h3>
              </div>
              <p className="text-sm text-stone-300 leading-relaxed">
                לטעון את <strong className="text-amber-200 break-all">{confirmUpload.file.name}</strong>{" "}
                ({_bytes(confirmUpload.file.size)}) ולשחזר ממנו?
              </p>
              <ul className="text-[11px] text-stone-400 list-disc pe-4 space-y-0.5">
                <li>המצב הנוכחי יישמר אוטומטית כגיבוי בטיחות</li>
                <li>כל הנתונים הקיימים <strong className="text-red-300">יידרסו</strong> במה שיש בקובץ</li>
                <li>הקובץ ייעלה גם ללוח השנה כגיבוי חדש (תג "מקובץ")</li>
              </ul>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setConfirmUpload(null)} disabled={uploading}
                  className="px-3 py-1.5 rounded text-[12px] font-bold text-stone-300 hover:bg-stone-800"
                  data-testid="upload-restore-cancel"
                >בטל</button>
                <button type="button" onClick={onConfirmUpload} disabled={uploading}
                  className="inline-flex items-center gap-1 px-4 py-1.5 rounded text-[12px] font-extrabold bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50"
                  data-testid="upload-restore-confirm"
                >
                  {uploading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  כן, שחזר מהקובץ
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Restore confirmation modal */}
        {confirmRestore && (
          <div className="fixed inset-0 z-[10110] bg-black/70 flex items-center justify-center p-4" onClick={() => !restoring && setConfirmRestore(null)}>
            <div dir="rtl" onClick={(e) => e.stopPropagation()} className="bg-stone-900 rounded-2xl border border-red-600/40 max-w-md w-full p-5 space-y-3">
              <div className="flex items-center gap-2 text-red-300">
                <AlertTriangle className="w-5 h-5" />
                <h3 className="text-base font-extrabold">אישור שחזור</h3>
              </div>
              <p className="text-sm text-stone-300 leading-relaxed">
                לפני שחזור ל-<strong className="text-emerald-300">
                  {confirmRestore.date} {new Date(confirmRestore.created_at).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </strong>,
                המערכת תיצור גיבוי בטיחות של המצב הנוכחי. תוכלי לבטל את השחזור בכל רגע.
              </p>
              <ul className="text-[11px] text-stone-400 list-disc pe-4 space-y-0.5">
                <li>{Object.keys(confirmRestore.counts || {}).filter((k) => k !== "__assets__").length} קולקציות יוחזרו</li>
                <li>{_bytes(confirmRestore.file_size)} ייקראו מהדיסק</li>
                <li>נתונים שנוספו אחרי {confirmRestore.date} <strong className="text-red-300">יידרסו</strong></li>
              </ul>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setConfirmRestore(null)} disabled={restoring}
                  className="px-3 py-1.5 rounded text-[12px] font-bold text-stone-300 hover:bg-stone-800"
                  data-testid="smart-restore-cancel"
                >בטל</button>
                <button type="button" onClick={onConfirmRestore} disabled={restoring}
                  className="inline-flex items-center gap-1 px-4 py-1.5 rounded text-[12px] font-extrabold bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
                  data-testid="smart-restore-confirm"
                >
                  {restoring && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  כן, שחזר
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Post-restore report */}
        {postReport && (
          <div className="fixed inset-0 z-[10120] bg-black/70 flex items-center justify-center p-4" onClick={() => setPostReport(null)}>
            <div dir="rtl" onClick={(e) => e.stopPropagation()} className="bg-stone-900 rounded-2xl border border-emerald-600/40 max-w-md w-full p-5 space-y-3">
              <div className="flex items-center gap-2 text-emerald-300">
                <CheckCircle2 className="w-5 h-5" />
                <h3 className="text-base font-extrabold">השחזור הושלם</h3>
              </div>
              <div className="text-sm text-stone-300 space-y-1">
                <div>סה"כ שורות שוחזרו: <strong className="text-emerald-200">{Object.values(postReport.counts || {}).reduce((a, b) => a + b, 0).toLocaleString("he-IL")}</strong></div>
                {postReport.validation?.score != null && (
                  <div>ציון תאימות: <strong className="text-emerald-200">{postReport.validation.score}/100</strong></div>
                )}
                {postReport.pre_restore_snapshot?.id && (
                  <div className="text-[11px] text-stone-400 bg-amber-900/20 border border-amber-700/30 rounded px-2 py-1">
                    💾 גיבוי בטיחות נוצר: <span dir="ltr" className="text-amber-200">{postReport.pre_restore_snapshot.date}</span> — ניתן לבטל את השחזור.
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <button type="button" onClick={() => setPostReport(null)}
                  className="px-4 py-1.5 rounded text-[12px] font-extrabold bg-emerald-600 hover:bg-emerald-500 text-white"
                  data-testid="smart-restore-done"
                >סגור</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
